import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Upload, Trash2, Plus, CheckCircle2, CalendarIcon } from "lucide-react";
import PoweredBy from "@/components/PoweredBy";
import RefundCalculator from "@/components/RefundCalculator";

const RELATIONS = ["Son", "Daughter", "Step-child", "Foster child", "Grandchild", "Sibling", "Niece/Nephew", "Parent", "Grandparent", "Other"];
const UPLOAD_CATEGORIES = [
  { key: "drivers_license", label: "Driver's license / ID" },
  { key: "ssn_proof", label: "Social Security cards (SSN)" },
  { key: "proof_of_residency", label: "Proof of residency" },
  { key: "form_1098t", label: "1098-T forms" },
  { key: "form_1095", label: "1095 forms" },
  { key: "form_w2", label: "W-2 forms" },
  { key: "form_1099", label: "1099 forms" },
  { key: "child_care", label: "Child care forms" },
  { key: "self_employment_receipts", label: "Self-employment receipts" },
  { key: "charitable_donations", label: "Charitable donation receipts" },
  { key: "mortgage_forms", label: "Mortgage forms (1098)" },
  { key: "retirement_forms", label: "Retirement forms (1099-R, 5498)" },
];

const EMPTY_DEP = { first_name: "", last_name: "", dob: "", ssn: "", relation: "" };

export default function Intake() {
  const { branding } = useBranding();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCat, setUploadingCat] = useState(null);
  const [done, setDone] = useState(null);

  const [f, setF] = useState({
    first_name: "", last_name: "", dob: "", ssn: "", address: "", city: "", state: "", zip: "", phone: "", email: "",
    filing_status: "", spouse_first_name: "", spouse_last_name: "", spouse_dob: "", spouse_ssn: "",
    dependents: [],
    w2_employers: "", ten99_payers: "", self_employment: "", rental_income: "", investment_income: "",
    itemize: false, itemized_mortgage: "", itemized_property_tax: "", itemized_medical: "", itemized_charity: "",
    credit_childcare: "", credit_education: "", credit_ev: "", credit_energy: "",
    bank_routing: "", bank_account: "", bank_account_type: "",
    refund_method: "", bank_name: "",
    uploads: {},
    disclaimer_accuracy: false, disclaimer_cash_advance: false,
    cash_advance_expected_refund: "",
    referred_by_code: "",
    signature: "", signed_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  // Pick up ?ref=CODE from the URL so referral links "just work"
  useEffect(() => {
    const ref = (searchParams.get("ref") || "").toUpperCase();
    if (ref && ref.length === 6) {
      setF(prev => ({ ...prev, referred_by_code: ref }));
    }
  }, [searchParams]);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const addDep = () => setF(prev => ({ ...prev, dependents: [...prev.dependents, { ...EMPTY_DEP }] }));
  const setDep = (i, k, v) => setF(prev => ({
    ...prev, dependents: prev.dependents.map((d, idx) => idx === i ? { ...d, [k]: v } : d),
  }));
  const removeDep = (i) => setF(prev => ({ ...prev, dependents: prev.dependents.filter((_, idx) => idx !== i) }));

  const uploadFiles = async (category, filelist) => {
    if (!filelist || !filelist.length) return;
    setUploadingCat(category);
    const added = [];
    for (const file of Array.from(filelist)) {
      if (file.size > 20 * 1024 * 1024) { toast.error(`${file.name}: file too large (20 MB max)`); continue; }
      const form = new FormData();
      form.append("file", file);
      try {
        const r = await api.post(`/intake/upload?category=${category}`, form, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ path: r.data.path, filename: file.name });
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
    }
    if (added.length) {
      setF(prev => ({
        ...prev,
        uploads: { ...prev.uploads, [category]: [...(prev.uploads[category] || []), ...added.map(a => a.path)] },
      }));
      toast.success(`${added.length} file${added.length === 1 ? "" : "s"} uploaded`);
    }
    setUploadingCat(null);
  };

  const removeUpload = (category, idx) => {
    setF(prev => ({
      ...prev,
      uploads: { ...prev.uploads, [category]: (prev.uploads[category] || []).filter((_, i) => i !== idx) },
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.disclaimer_accuracy) { toast.error("Please accept the accuracy disclaimer"); return; }
    if (!f.signature.trim()) { toast.error("Please type your name to sign"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/intake", f);
      setDone({ id: r.data.id, access_code: r.data.access_code });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background paper-texture p-6 py-12">
        <div className="max-w-2xl mx-auto space-y-8">
          <div className="bg-card border border-border rounded-md p-10 text-center" data-testid="intake-success">
            <CheckCircle2 size={56} className="mx-auto text-primary mb-4" />
            <div className="label-caps mb-2">Received</div>
            <h1 className="font-serif text-4xl mb-3">Thank you, {f.first_name}!</h1>
            <p className="text-muted-foreground mb-6">Your intake is in. A preparer will review your paperwork and reach out within 1-2 business days.</p>

            <div className="rounded-md border border-primary/40 bg-primary/5 p-4 mb-6 text-left">
              <div className="label-caps text-[10px] mb-2 text-primary">Save these · you'll need them to check status</div>
              <div className="space-y-1 text-sm">
                <div>Reference #: <span className="font-mono">{done.id.slice(0, 8)}</span></div>
                <div>Access code: <span className="font-mono text-lg tracking-widest" data-testid="intake-access-code">{done.access_code}</span></div>
                <div className="text-xs text-muted-foreground pt-1">Email on file: {f.email}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-6">
              Check your return's progress any time at <Link to="/client-portal" className="underline text-foreground">the client portal</Link>.
            </p>
            <Button onClick={() => nav("/")} className="w-full" data-testid="intake-done-btn">Back to homepage</Button>
          </div>

          <div>
            <div className="label-caps text-primary mb-3 text-center">While you wait — estimate your refund</div>
            <RefundCalculator compact context="intake_success" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <div className="label-caps hidden sm:block">Client Intake</div>
        </div>
      </header>

      <form onSubmit={submit} className="max-w-3xl mx-auto p-6 lg:p-12 space-y-8" data-testid="intake-form">
        <div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-3">Tax return intake.</h1>
          <p className="text-muted-foreground max-w-xl">Fill this out completely — the more accurate up front, the faster your return goes out the door. Everything is confidential.</p>
        </div>

        {/* Personal */}
        <Section title="Personal information">
          <Row>
            <Field label="First name" required>
              <Input required data-testid="intake-first-name" value={f.first_name} onChange={e => set("first_name", e.target.value)} />
            </Field>
            <Field label="Last name" required>
              <Input required data-testid="intake-last-name" value={f.last_name} onChange={e => set("last_name", e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Field label="Date of birth"><Input type="date" value={f.dob} onChange={e => set("dob", e.target.value)} /></Field>
            <Field label="SSN"><Input placeholder="XXX-XX-XXXX" value={f.ssn} onChange={e => set("ssn", e.target.value)} /></Field>
          </Row>
          <Field label="Street address"><Input value={f.address} onChange={e => set("address", e.target.value)} /></Field>
          <Row>
            <Field label="City"><Input value={f.city} onChange={e => set("city", e.target.value)} /></Field>
            <Field label="State"><Input value={f.state} onChange={e => set("state", e.target.value)} /></Field>
            <Field label="ZIP"><Input value={f.zip} onChange={e => set("zip", e.target.value)} /></Field>
          </Row>
          <Row>
            <Field label="Phone"><Input type="tel" value={f.phone} onChange={e => set("phone", e.target.value)} /></Field>
            <Field label="Email" required><Input type="email" required data-testid="intake-email" value={f.email} onChange={e => set("email", e.target.value)} /></Field>
          </Row>
        </Section>

        {/* Filing status */}
        <Section title="Filing status">
          <Field label="Filing status" required>
            <select
              required
              data-testid="intake-filing-status"
              value={f.filing_status}
              onChange={e => set("filing_status", e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Choose one…</option>
              <option value="single">Single</option>
              <option value="mfj">Married filing jointly</option>
              <option value="mfs">Married filing separately</option>
              <option value="hoh">Head of household</option>
              <option value="qw">Qualifying surviving spouse</option>
            </select>
          </Field>
          {f.filing_status === "mfj" && (
            <>
              <Row>
                <Field label="Spouse first name"><Input value={f.spouse_first_name} onChange={e => set("spouse_first_name", e.target.value)} /></Field>
                <Field label="Spouse last name"><Input value={f.spouse_last_name} onChange={e => set("spouse_last_name", e.target.value)} /></Field>
              </Row>
              <Row>
                <Field label="Spouse DOB"><Input type="date" value={f.spouse_dob} onChange={e => set("spouse_dob", e.target.value)} /></Field>
                <Field label="Spouse SSN"><Input value={f.spouse_ssn} onChange={e => set("spouse_ssn", e.target.value)} /></Field>
              </Row>
            </>
          )}
        </Section>

        {/* Dependents */}
        <Section title="Dependents">
          <p className="text-sm text-muted-foreground -mt-2 mb-2">Add anyone who lived with you and you supported.</p>
          <div className="space-y-3">
            {f.dependents.map((d, i) => (
              <Card key={i} className="p-4" data-testid={`intake-dependent-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="label-caps">Dependent #{i + 1}</div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeDep(i)} data-testid={`remove-dep-${i}`}><Trash2 size={12} /></Button>
                </div>
                <Row>
                  <Field label="First name"><Input value={d.first_name} onChange={e => setDep(i, "first_name", e.target.value)} /></Field>
                  <Field label="Last name"><Input value={d.last_name} onChange={e => setDep(i, "last_name", e.target.value)} /></Field>
                </Row>
                <Row>
                  <Field label="DOB"><Input type="date" value={d.dob} onChange={e => setDep(i, "dob", e.target.value)} /></Field>
                  <Field label="SSN"><Input value={d.ssn} onChange={e => setDep(i, "ssn", e.target.value)} /></Field>
                </Row>
                <Field label="Relation to client">
                  <select
                    value={d.relation}
                    onChange={e => setDep(i, "relation", e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid={`dep-relation-${i}`}
                  >
                    <option value="">Choose one…</option>
                    {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
              </Card>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addDep} className="mt-3" data-testid="add-dependent-btn">
            <Plus size={12} className="mr-1" /> Add dependent
          </Button>
        </Section>

        {/* Income */}
        <Section title="Income sources">
          <Field label="W-2 employers (list each, one per line)"><Textarea rows={2} value={f.w2_employers} onChange={e => set("w2_employers", e.target.value)} /></Field>
          <Field label="1099 payers (contractor income)"><Textarea rows={2} value={f.ten99_payers} onChange={e => set("ten99_payers", e.target.value)} /></Field>
          <Field label="Self-employment (describe your business)"><Textarea rows={2} value={f.self_employment} onChange={e => set("self_employment", e.target.value)} /></Field>
          <Row>
            <Field label="Rental income"><Input value={f.rental_income} onChange={e => set("rental_income", e.target.value)} /></Field>
            <Field label="Investment income"><Input value={f.investment_income} onChange={e => set("investment_income", e.target.value)} /></Field>
          </Row>
        </Section>

        {/* Deductions & Credits */}
        <Section title="Deductions & credits">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.itemize} onChange={e => set("itemize", e.target.checked)} data-testid="intake-itemize" />
            <span>I want to itemize my deductions (instead of the standard deduction)</span>
          </label>
          {f.itemize && (
            <div className="space-y-3 mt-2 pl-6 border-l border-border">
              <Row>
                <Field label="Mortgage interest"><Input value={f.itemized_mortgage} onChange={e => set("itemized_mortgage", e.target.value)} /></Field>
                <Field label="Property tax"><Input value={f.itemized_property_tax} onChange={e => set("itemized_property_tax", e.target.value)} /></Field>
              </Row>
              <Row>
                <Field label="Medical expenses"><Input value={f.itemized_medical} onChange={e => set("itemized_medical", e.target.value)} /></Field>
                <Field label="Charitable donations"><Input value={f.itemized_charity} onChange={e => set("itemized_charity", e.target.value)} /></Field>
              </Row>
            </div>
          )}
          <Row>
            <Field label="Child care expenses"><Input value={f.credit_childcare} onChange={e => set("credit_childcare", e.target.value)} /></Field>
            <Field label="Education expenses"><Input value={f.credit_education} onChange={e => set("credit_education", e.target.value)} /></Field>
          </Row>
          <Row>
            <Field label="EV credit (new EV purchase?)"><Input value={f.credit_ev} onChange={e => set("credit_ev", e.target.value)} /></Field>
            <Field label="Energy credit (solar, heat pump, etc.)"><Input value={f.credit_energy} onChange={e => set("credit_energy", e.target.value)} /></Field>
          </Row>
        </Section>

        {/* Uploads */}
        <Section title="Upload your documents">
          <p className="text-sm text-muted-foreground -mt-2 mb-3">If these documents pertain to you, please upload everything so it won't delay in processing your return. PDF, PNG, JPG, or HEIC — 20 MB per file.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {UPLOAD_CATEGORIES.map(c => (
              <Card key={c.key} className="p-4">
                <div className="text-sm font-medium mb-1">{c.label}</div>
                <div className="text-[10px] text-muted-foreground mb-2">
                  {(f.uploads[c.key] || []).length} file{(f.uploads[c.key] || []).length === 1 ? "" : "s"} attached
                </div>
                <label className="inline-flex w-full">
                  <input
                    type="file"
                    multiple
                    accept="application/pdf,image/*"
                    className="hidden"
                    data-testid={`intake-upload-${c.key}`}
                    onChange={e => uploadFiles(c.key, e.target.files)}
                  />
                  <Button asChild size="sm" variant="outline" className="w-full" disabled={uploadingCat === c.key}>
                    <span><Upload size={12} className="mr-1" /> {uploadingCat === c.key ? "Uploading…" : "Choose files"}</span>
                  </Button>
                </label>
                {(f.uploads[c.key] || []).map((p, idx) => (
                  <div key={idx} className="text-[10px] font-mono mt-1 flex items-center justify-between gap-2">
                    <span className="truncate min-w-0">…{p.slice(-24)}</span>
                    <button type="button" onClick={() => removeUpload(c.key, idx)} className="text-destructive" data-testid={`remove-upload-${c.key}-${idx}`} aria-label="Remove"><Trash2 size={10} /></button>
                  </div>
                ))}
              </Card>
            ))}
          </div>
        </Section>

        {/* Refund delivery */}
        <Section title="How would you like your refund?">
          <div className="rounded-md border border-primary/30 bg-primary/5 p-4 mb-5 text-sm leading-relaxed" data-testid="intake-bank-product-note">
            <div className="label-caps text-[10px] mb-1 text-primary">About the Bank Product</div>
            <p>
              All returns are sent to <strong>Refund Advantage</strong>. When you look at the bank paperwork you'll see a <strong>routing and account number from RA</strong> — that's normal. Refund Advantage issues your refund from the IRS on the date you're set to be funded. This is called a <strong>Bank Product</strong>.
            </p>
          </div>

          <Row>
            <Field label="Refund method">
              <select
                value={f.refund_method}
                onChange={e => set("refund_method", e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="intake-refund-method"
              >
                <option value="">Choose one…</option>
                <option value="ra_card">Refund Advantage Card</option>
                <option value="direct_deposit">Direct Deposit to my bank</option>
                <option value="ra_check">Check from Refund Advantage</option>
              </select>
            </Field>
          </Row>

          {f.refund_method === "direct_deposit" && (
            <div className="mt-4 space-y-4" data-testid="intake-dd-fields">
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs leading-relaxed text-destructive">
                <strong>Heads up:</strong> If the wrong routing or account number is entered, it <strong>will delay your refund</strong> and it will automatically be sent as a <strong>CHECK from Refund Advantage</strong>. Double-check every number.
              </div>
              <Row>
                <Field label="Bank name">
                  <Input value={f.bank_name} onChange={e => set("bank_name", e.target.value)} placeholder="e.g., Wells Fargo" data-testid="intake-bank-name" />
                </Field>
                <Field label="Routing number (9 digits)">
                  <Input inputMode="numeric" maxLength={9} value={f.bank_routing} onChange={e => set("bank_routing", e.target.value)} data-testid="intake-bank-routing" />
                </Field>
              </Row>
              <Row>
                <Field label="Account number">
                  <Input inputMode="numeric" value={f.bank_account} onChange={e => set("bank_account", e.target.value)} data-testid="intake-bank-account" />
                </Field>
                <Field label="Account type">
                  <select value={f.bank_account_type} onChange={e => set("bank_account_type", e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="intake-bank-type">
                    <option value="">Choose…</option>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </Field>
              </Row>
            </div>
          )}
        </Section>

        {/* Notes */}
        <Section title="Anything else we should know?">
          <Textarea rows={3} value={f.notes} onChange={e => set("notes", e.target.value)} placeholder="Life changes, new baby, moved states, sold a house, etc." />
        </Section>

        {/* Referral */}
        <Section title="Referred by a friend?">
          <p className="text-sm text-muted-foreground -mt-2 mb-2">
            If a {branding.brand_name} client sent you their access code, enter it here — both of you get <strong>$25 off</strong> your next return once yours is filed.
          </p>
          <Field label="Referral code (6 characters)">
            <Input
              value={f.referred_by_code}
              onChange={e => set("referred_by_code", e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="ABC123"
              className="font-mono tracking-widest max-w-xs"
              data-testid="intake-referral-code"
            />
          </Field>
        </Section>

        {/* Disclaimers */}
        <Section title="Disclaimers & signature">
          <div className="rounded-md bg-secondary/40 border border-border p-4 text-sm">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={f.disclaimer_accuracy} onChange={e => set("disclaimer_accuracy", e.target.checked)} data-testid="disclaimer-accuracy" className="mt-1" />
              <span>
                <strong>{branding.brand_name} is not responsible for any letters or audits if information is withheld.</strong> Only things that will be inputted to your return will be the information provided to your preparer. If you agree, sign and date your intake form below.
              </span>
            </label>
          </div>
          <div className="rounded-md bg-secondary/40 border border-border p-4 text-sm">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={f.disclaimer_cash_advance} onChange={e => set("disclaimer_cash_advance", e.target.checked)} data-testid="disclaimer-cash-advance" className="mt-1" />
              <span>
                <strong>Cash advance disclaimer:</strong> If you apply for a cash advance, {branding.brand_name} is not responsible for the outcome of the decision. It is solely up to <strong>Refund Advantage</strong> — the tax bank {branding.brand_name} is affiliated with (also known as <strong>Pathway</strong>). <em>Only check this box if you're applying for a cash advance.</em>
              </span>
            </label>
          </div>
          {f.disclaimer_cash_advance && (
            <div className="rounded-md border border-primary/40 bg-primary/5 p-4 text-sm space-y-3" data-testid="cash-advance-details">
              <div className="label-caps text-[10px] text-primary">Cash advance application</div>
              <p className="text-xs text-muted-foreground">
                Enter your best estimate of your federal refund — this helps Refund Advantage decide how much they can advance you (typically up to $6,000 or ~50% of the estimated refund, whichever is less).
              </p>
              <Field label="Estimated federal refund ($)">
                <Input
                  inputMode="numeric"
                  placeholder="4,500"
                  value={f.cash_advance_expected_refund}
                  onChange={e => set("cash_advance_expected_refund", e.target.value)}
                  data-testid="cash-advance-expected"
                />
              </Field>
              <p className="text-[10px] text-muted-foreground">
                Make sure you've uploaded your driver's license / state ID above (under Documents) — Refund Advantage requires it before deciding.
              </p>
            </div>
          )}
          <Row>
            <Field label="Type your full name to sign" required>
              <Input required data-testid="intake-signature" value={f.signature} onChange={e => set("signature", e.target.value)} placeholder="Your legal name" />
            </Field>
            <Field label="Date">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start font-normal"
                    data-testid="intake-signed-date-btn"
                  >
                    <CalendarIcon size={14} className="mr-2" />
                    {f.signed_date
                      ? new Date(f.signed_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                      : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={f.signed_date ? new Date(f.signed_date + "T00:00:00") : undefined}
                    onSelect={(d) => {
                      if (!d) return;
                      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                      set("signed_date", iso);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
          </Row>
        </Section>

        <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-border">
          <Button type="button" variant="ghost" onClick={() => nav("/")}>Back</Button>
          <Button type="submit" size="lg" disabled={submitting} data-testid="intake-submit-btn">
            {submitting ? "Submitting…" : "Submit intake"}
          </Button>
        </div>

        <PoweredBy className="text-center mt-6" />
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <div className="border-b border-border pb-2">
      <h2 className="font-serif text-2xl">{title}</h2>
    </div>
    {children}
  </section>
);

const Row = ({ children }) => <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;

const Field = ({ label, required, children }) => (
  <div className="space-y-1">
    <Label className="label-caps text-[10px]">{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
    {children}
  </div>
);
